import * as React from 'react';
import cn from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Header, ModalX } from '../../../BaseComponents';

import { Group } from '../../../Autocomplete/types';
import { Location } from '../../types';

import { useTheme } from '../../../theme';

import { useIsMobile } from '../../../utils';

interface Props {
	isOpen: boolean;
	countries: Group<Location>[];
	onClose: () => void;
	onSelect: (location: Location) => void;
}

const DirectionsDialog: React.FunctionComponent<Props> = ({ countries, isOpen, onClose, onSelect }) => {
	const { DirectionsDialog: css } = useTheme('LocationAutocomplete');

	const { t } = useTranslation('LocationAutocomplete');

	const isMobile = useIsMobile();

	const [selectedCountry, setSelectedCountry] = useState<Group<Location> | undefined>();

	const showCityList = !isMobile || !!selectedCountry;
	const showCountryList = !isMobile || !selectedCountry;

	const onCountrySelect = (country?: Group<Location>) => {
		setSelectedCountry(country);
	};

	const clearCountrySelection = () => setSelectedCountry(undefined);

	const onCitySelect = (location: Location) => () => {
		onSelect(location);
		onClose();
	};

	React.useEffect(() => {
		if (!isOpen) {
			return;
		}

		if (isMobile) {
			clearCountrySelection();
		} else {
			setSelectedCountry(countries[0]);
		}
	}, [isOpen]);

	return (
		<ModalX
			isOpen={isOpen}
			size={isMobile ? 'fullscreen' : 'xlarge'}
			onClose={onClose}
			noPadding
			enableSwipeToClose={false}
			classes={{
				root: css.root,
				top_wrapper: css.top_wrapper,
				content: css.content,
				closeButton: css.closeButton
			}}
		>
			<div className={css.dialog}>
				{showCountryList && (
					<div className={css.countries}>
						<Header
							className={css.label}
							showEmptySlots={false}
							center={<div className={css.labelText}>{t('Pick a country')}</div>}
						/>

						<div className={css.countries__list}>
							{countries?.map(country => (
								<div
									key={country.label.toString()}
									data-country={country.label}
									className={cn(css.country, {
										[css.country_active]: country.label === selectedCountry?.label
									})}
									onClick={() => onCountrySelect(country)}
								>
									{country.label}
								</div>
							))}
						</div>
					</div>
				)}

				{showCityList && (
					<div className={css.cities}>
						<Header
							className={css.label}
							showEmptySlots={false}
							onGoBackward={clearCountrySelection}
							center={<div className={css.labelText}>{t('Pick a city')}</div>}
						/>

						<div className={css.cities__list}>
							{selectedCountry?.options?.map(({ value: city, label }, index) => {
								return (
									<div key={index} className={css.city} onClick={onCitySelect(city)}>
										<div>
											<div>{label}</div>

											{city.stopPointsCount > 0 && (
												<div className={css.stops}>
													{city.stopPointsCount}{' '}
													{t('Stop', {
														count: city.stopPointsCount
													})}
												</div>
											)}
										</div>

										<span className={css.iata}>{city.iata}</span>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</ModalX>
	);
};

export default DirectionsDialog;
